import { api } from './client';

export type CampaignStatus =
  | 'draft' | 'scheduled' | 'running' | 'paused' | 'sent' | 'completed' | 'archived';
export type CampaignType = 'oneshot' | 'automated' | 'recurring';
export type Channel = 'mail' | 'sms' | 'wa' | 'modal' | 'notif';

export interface DashboardService {
  key: string;
  status: 'ok' | 'fail' | 'running' | 'never';
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  counter: number;
  lastError: string | null;
}
export interface MarketingDashboard {
  services: DashboardService[];
  queue: { pending: number; processing: number; total: number; failed: number; skipped: number; sent: number };
  stats: { sent30d: number; sent7d: number; errors30d: number; errors7d: number };
  activeCampaigns: number;
}

export interface CampaignListItem {
  id: number;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  channels: Channel[];
  country: string | null;
  segmentId: number | null;
  segmentName: string | null;
  segmentColor: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  recipientsEstimate: number | null;
  recipients: number;
  jobsDone: number;
  deliveredTotal: number;
  opensUnique: number;
  clicksTotal: number;
  uniqueClickers: number;
  ctr: number | null;
  createdAt: string;
}

export interface CampaignJobStateCounts {
  pending: number; enriching: number; enriched: number; sending: number;
  sent: number; failed: number; skipped: number;
}
export interface CampaignDetail extends CampaignListItem {
  description: string | null;
  subject: string | null;
  senderId: number | null;
  htmlBody: string | null;
  deliveredTotal: number;
  bouncesTotal: number;
  unsubscribesTotal: number;
  opensTotal: number;
  jobStates: CampaignJobStateCounts;
  meta: Record<string, unknown> | null;
  createdBy: string | null;
}
export interface CampaignJob {
  id: number;
  userId: number;
  channel: Channel;
  recipientAddress: string | null;
  state: string;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  externalId: string | null;
}

export interface ChannelCard {
  key: Channel | 'lp';
  label: string;
  status: 'active' | 'planned';
  countries: { code: string; active: boolean }[];
  sendersCount?: number;
  templatesCount?: number;
}

export interface Segment {
  id: number; name: string; color: string; country: string;
  priority: number; active: boolean; showInUsersList: boolean; rules: unknown[];
}
export interface Sender {
  id: number; country: string | null; purpose: 'notification' | 'campaign'; name: string; email: string;
  replyTo: string | null; isDefault: boolean; verified: boolean;
  deliverabilityScore: number | null; mailkitSenderId: string | null;
  provider: 'mailkit' | 'sendgrid'; warmupLimit: number | null; active: boolean;
  mailkitCampaignId: number | null; mailkitMailinglistId: number | null;
}
export interface SenderInput {
  country: string | null; purpose: 'notification' | 'campaign'; provider: 'mailkit' | 'sendgrid';
  name: string; email: string; replyTo: string | null; isDefault: boolean; active: boolean;
  warmupLimit: number | null; mailkitCampaignId: number | null; mailkitMailinglistId: number | null;
}
export interface ModalTemplate {
  id: number; code: string; name: string; country: string | null;
  title: string | null; theme: string; active: number | boolean;
}

const BASE = '/api/v1/rondo/marketing';

export type TemplateType = 'mail' | 'lp' | 'modal' | 'notif' | 'sms';
export interface TemplateListItem {
  id: number; type: TemplateType; name: string; code: string | null; country: string | null;
  active: number; archived: boolean; author_name: string | null; created_at: string | null;
  thumbnail_kind: 'html' | 'image' | 'text'; thumbnail_value: string | null;
  subject?: string; purpose?: string; sent_total?: number; [k: string]: unknown;
}
export interface TemplateListParams { country?: string; all?: boolean; q?: string }
const TBASE = `${BASE}/templates`;
export const getTemplateCounts = () => api.get<Record<TemplateType, number>>(`${TBASE}/counts`);
export const getTemplates = (type: TemplateType, p: TemplateListParams = {}) => {
  const qs = new URLSearchParams({ type });
  if (p.country) qs.set('country', p.country);
  if (p.all) qs.set('all', '1');
  if (p.q) qs.set('q', p.q);
  return api.get<{ type: TemplateType; templates: TemplateListItem[] }>(`${TBASE}?${qs.toString()}`);
};
export const getTemplate = (type: TemplateType, id: number) =>
  api.get<Record<string, unknown>>(`${TBASE}/${type}/${id}`);
export const createTemplate = (type: TemplateType, body: Record<string, unknown>) =>
  api.post<{ id: number; type: TemplateType }>(`${TBASE}/${type}`, body);
export const updateTemplate = (type: TemplateType, id: number, body: Record<string, unknown>) =>
  api.put<{ updated: boolean }>(`${TBASE}/${type}/${id}`, body);
export const archiveTemplate = (type: TemplateType, id: number) =>
  api.del<{ ok: boolean }>(`${TBASE}/${type}/${id}`);
export const restoreTemplate = (type: TemplateType, id: number) =>
  api.put<{ updated: boolean }>(`${TBASE}/${type}/${id}`, { active: true });
export const duplicateTemplate = (type: TemplateType, id: number) =>
  api.post<{ id: number; name: string; type: TemplateType }>(`${TBASE}/${type}/${id}/duplicate`);

export const getMarketingDashboard = () => api.get<MarketingDashboard>(`${BASE}/dashboard`);
export const getCampaigns = () => api.get<CampaignListItem[]>(`${BASE}/campaigns`);
export const getCampaignDetail = (id: number) => api.get<CampaignDetail>(`${BASE}/campaigns/${id}`);
export const getCampaignJobs = (id: number, limit?: number) =>
  api.get<CampaignJob[]>(`${BASE}/campaigns/${id}/jobs${limit ? `?limit=${limit}` : ''}`);
export const getChannels = () => api.get<ChannelCard[]>(`${BASE}/channels`);
export const getSegments = (country?: string) =>
  api.get<Segment[]>(`${BASE}/segments${country ? `?country=${country}` : ''}`);
export const getSenders = () => api.get<Sender[]>(`${BASE}/senders`);
export const createSender = (body: SenderInput) => api.post<{ id: number }>(`${BASE}/senders`, body);
export const updateSender = (id: number, body: SenderInput) => api.patch<{ updated: boolean }>(`${BASE}/senders/${id}`, body);
export const getSegmentEstimate = (id: number) =>
  api.get<{ count: number }>(`${BASE}/segments/${id}/estimate`);

export interface SegmentRuleInput { metric: string; op: string; value: number }
export interface SegmentInput {
  name: string; country: string; color?: string; priority?: number; active?: boolean; rules?: SegmentRuleInput[];
}
export const createSegment = (body: SegmentInput) =>
  api.post<{ id: number }>(`${BASE}/segments`, body);
export const updateSegment = (id: number, body: Partial<SegmentInput>) =>
  api.patch<{ updated: boolean }>(`${BASE}/segments/${id}`, body);
export const deactivateSegment = (id: number) =>
  api.del<{ deactivated: boolean }>(`${BASE}/segments/${id}`);
export const getModalTemplates = (country?: string) =>
  api.get<ModalTemplate[]>(`${BASE}/modal-templates${country ? `?country=${country}` : ''}`);

export interface MailTemplate {
  id: number; code: string; lang: string; name: string; subject: string | null;
  purpose: string; enabled: number | boolean; sent_total: number; sent_last_at: string | null;
}
export const getMailTemplates = () => api.get<MailTemplate[]>(`${BASE}/mail-templates`);

export interface Partner { id: number; slug: string; name: string; country: string | null; }
export const getPartners = (country?: string) =>
  api.get<Partner[]>(`${BASE}/partners${country ? `?country=${country}` : ''}`);

export interface CreateCampaignBody {
  name: string;
  type?: CampaignType;
  channels: Channel[];
  country?: string | null;
  segmentId?: number | null;
  senderId?: number | null;
  subject?: string | null;
  htmlBody?: string | null;
  scheduledAt?: string | null;
  // Kanálová konfigurace pro non-mail kanály: { sms:{body,sender}, notif:{message,url} }.
  meta?: Record<string, unknown> | null;
}
export const createCampaign = (body: CreateCampaignBody) =>
  api.post<{ id: number }>(`${BASE}/campaigns`, body);
export const testSendCampaign = (body: { to: string; html: string; subject: string; country: string; senderId: number }) =>
  api.post<{ mode: string; note: string }>(`${BASE}/campaigns/test-send`, body);
export const updateCampaign = (id: number, body: Partial<CreateCampaignBody> & { status?: CampaignStatus }) =>
  api.patch<{ updated: boolean }>(`${BASE}/campaigns/${id}`, body);
export const archiveCampaign = (id: number) =>
  api.del<{ archived: boolean }>(`${BASE}/campaigns/${id}`);
export const duplicateCampaign = (id: number) =>
  api.post<{ id: number }>(`${BASE}/campaigns/${id}/duplicate`, {});

export interface CampaignConversions {
  synced_at: string;
  clickers_count: number;
  total_leads: number;
  by_partner: Record<string, { clicks: number; unique_clickers: number; leads: number }>;
  note?: string;
  capped?: boolean;
  from_cache?: boolean;
  cache_age_sec?: number;
}
export const getCampaignConversions = (id: number, refresh = false) =>
  api.get<CampaignConversions>(`${BASE}/campaigns/${id}/conversions${refresh ? '?refresh=1' : ''}`);

export async function uploadAssetImage(file: File): Promise<{ url: string; filename: string }> {
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ''));
    r.onerror = () => reject(new Error('Soubor se nepodařilo načíst'));
    r.readAsDataURL(file);
  });
  return api.post<{ url: string; filename: string }>(`${BASE}/assets`, { mime: file.type, dataBase64 });
}

export interface Shortener {
  id: number;
  hostname: string;
  serverIp: string | null;
  description: string | null;
  sslEnabled: boolean;
  channels: string;
  active: boolean;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}
export interface CreateShortenerBody {
  hostname: string;
  server_ip?: string;
  description?: string;
  channels?: string;
  ssl_enabled?: boolean;
  notes?: string;
}
export type UpdateShortenerBody = Partial<Omit<CreateShortenerBody, 'hostname'>> & { active?: boolean };

export const getShorteners = () => api.get<Shortener[]>(`${BASE}/shorteners`);
export const createShortener = (body: CreateShortenerBody) =>
  api.post<{ id: number }>(`${BASE}/shorteners`, body);
export const updateShortener = (id: number, body: UpdateShortenerBody) =>
  api.patch<{ updated: boolean }>(`${BASE}/shorteners/${id}`, body);

export interface LandingDomain {
  id: number;
  country: string;
  hostname: string;
  sftpUser: string | null;
  sftpHost: string | null;
  webroot: string | null;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}
export interface CreateLandingDomainBody {
  country: string;
  hostname: string;
  sftp_user?: string;
  sftp_host?: string;
  webroot?: string;
}
export type UpdateLandingDomainBody = Partial<Omit<CreateLandingDomainBody, 'country' | 'hostname'>> & { active?: boolean };

export const getLandingDomains = () => api.get<LandingDomain[]>(`${BASE}/landing-domains`);
export const createLandingDomain = (body: CreateLandingDomainBody) =>
  api.post<{ id: number }>(`${BASE}/landing-domains`, body);
export const updateLandingDomain = (id: number, body: UpdateLandingDomainBody) =>
  api.patch<{ updated: boolean }>(`${BASE}/landing-domains/${id}`, body);
